import { login } from "@lib/data/customer"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import { useActionState } from "react"
import { Mail, Lock, Eye } from "lucide-react"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Login = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(login, null)

  return (
    <div data-testid="login-page">
      <h2 className="text-2xl font-black text-center text-gray-900 mb-1 uppercase tracking-wide">
        Sign In With Email
      </h2>
      <p className="text-center text-sm text-gray-500 mb-8">
        Welcome back to the squad
      </p>

      <ErrorMessage
        error={formState?.error}
        data-testid="login-error-message"
        className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
      />

      <form className="w-full" action={formAction}>
        <div className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              E-Mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="email"
                type="email"
                placeholder="E-Mail"
                required
                autoComplete="email"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="current-password"
                className="w-full pl-11 pr-11 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none"
                data-testid="password-input"
              />
              <Eye className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 cursor-pointer" />
            </div>
          </div>
        </div>

        {/* Remember me + Forgot */}
        <div className="flex items-center justify-between mt-4 mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
            Remember me
          </label>
          <button type="button" className="text-sm text-brand-accent hover:underline font-medium">
            Forgotten Password?
          </button>
        </div>

        <SubmitButton
          data-testid="sign-in-button"
          className="w-full py-3.5 bg-[#1a1a2e] text-white font-bold rounded-lg hover:bg-[#2a2a3e] transition-colors flex items-center justify-center gap-2"
        >
          Login
          <span aria-hidden="true">&rarr;</span>
        </SubmitButton>
      </form>

      {/* OR divider */}
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <p className="text-center text-sm text-gray-500 mb-4">Sign In With...</p>

      {/* Social buttons */}
      <div className="grid grid-cols-1">
        <button
          type="button"
          className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Google
        </button>
      </div>
    </div>
  )
}

export default Login
